/* eslint-disable react/jsx-max-props-per-line */
/* eslint-disable react/jsx-sort-props */
import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { DialogDefaultV2, TabFormV2 } from '../../../components/v2';
import {
  getTableV2DetailBodyCellSx,
  getTableV2HeaderCellSx,
  tableV2EmptyStateSx,
  tableV2TableSx,
} from '../../../components/v2/tableV2Styles';
import { filterUnidadesByPosse, getPosseContagens } from './helpers';

const DETAIL_ROW_HEIGHT = 44;
const TAB_TODOS = 0;

const UnidadesTable = ({ unidades, onOpen }) => {
  const headerCellSx = getTableV2HeaderCellSx(DETAIL_ROW_HEIGHT);

  if (!unidades.length) {
    return (
      <Box sx={{ ...tableV2EmptyStateSx, py: 3, px: 2 }}>
        Nenhuma unidade neste filtro.
      </Box>
    );
  }

  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Table size="medium" aria-label="Unidades por posse" sx={tableV2TableSx}>
        <TableHead>
          <TableRow hover={false}>
            <TableCell sx={headerCellSx}>Patrimônio</TableCell>
            <TableCell sx={headerCellSx}>Série</TableCell>
            <TableCell sx={headerCellSx}>Último Repasse</TableCell>
            <TableCell sx={{ ...headerCellSx, width: 56 }} />
          </TableRow>
        </TableHead>
        <TableBody>
          {unidades.map((unidade, rowIndex) => {
            const cellSx = getTableV2DetailBodyCellSx(DETAIL_ROW_HEIGHT, rowIndex);
            return (
              <TableRow key={unidade.id} hover>
                <TableCell sx={cellSx}>{unidade.numPatrimonio || '—'}</TableCell>
                <TableCell sx={cellSx}>{unidade.numSerie || '—'}</TableCell>
                <TableCell sx={cellSx}>{unidade.ultimoRepasse || '—'}</TableCell>
                <TableCell sx={cellSx}>
                  <IconButton
                    size="small"
                    aria-label="Abrir unidade"
                    onClick={() => onOpen(unidade.id)}
                  >
                    <VisibilityOutlinedIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
};

UnidadesTable.propTypes = {
  unidades: PropTypes.arrayOf(PropTypes.object).isRequired,
  onOpen: PropTypes.func.isRequired,
};

const EquipamentoStatusDialog = ({ open, grupo, onClose, onOpenUnidade, zIndex }) => {
  const [tab, setTab] = useState(TAB_TODOS);

  useEffect(() => {
    if (open) setTab(TAB_TODOS);
  }, [open, grupo]);

  const unidades = grupo?.unidades || [];
  const quantidade = grupo?.quantidade ?? unidades.length;
  const { comEmpresa, comColaborador } = getPosseContagens(grupo || {});

  const unidadesTodos = useMemo(
    () => filterUnidadesByPosse(unidades, null),
    [unidades]
  );
  const unidadesEmpresa = useMemo(
    () => filterUnidadesByPosse(unidades, true),
    [unidades]
  );
  const unidadesColaborador = useMemo(
    () => filterUnidadesByPosse(unidades, false),
    [unidades]
  );

  const title = grupo
    ? `Status · ${grupo.tipoEquipamentoNome || 'Equipamento'}${grupo.modelo ? ` · ${grupo.modelo}` : ''}`
    : 'Status';

  const handleOpenUnidade = (unidadeId) => {
    onOpenUnidade(unidadeId);
  };

  return (
    <DialogDefaultV2
      open={open}
      handlerCancel={onClose}
      handlerConfirm={onClose}
      title={title}
      maxWidth="md"
      fullWidth
      readOnly
      zIndex={zIndex}
    >
      <Stack spacing={2} sx={{ px: { xs: 0.5, sm: 1 }, pb: 1 }}>
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
          <Typography variant="body2" color="text.secondary">
            Total: <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>{quantidade}</Box>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Eletropontes: <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>{comEmpresa}</Box>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Colaborador: <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>{comColaborador}</Box>
          </Typography>
        </Stack>

        <TabFormV2
          currentTab={tab}
          onChangeTab={setTab}
          tabPanels={[
            {
              label: `Todos (${unidadesTodos.length})`,
              element: (
                <UnidadesTable
                  unidades={unidadesTodos}
                  onOpen={handleOpenUnidade}
                />
              ),
            },
            {
              label: `Eletropontes (${unidadesEmpresa.length})`,
              element: (
                <UnidadesTable
                  unidades={unidadesEmpresa}
                  onOpen={handleOpenUnidade}
                />
              ),
            },
            {
              label: `Colaborador (${unidadesColaborador.length})`,
              element: (
                <UnidadesTable
                  unidades={unidadesColaborador}
                  onOpen={handleOpenUnidade}
                />
              ),
            },
          ]}
        />
      </Stack>
    </DialogDefaultV2>
  );
};

EquipamentoStatusDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  grupo: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onOpenUnidade: PropTypes.func.isRequired,
  zIndex: PropTypes.oneOfType([PropTypes.number, PropTypes.func]),
};

EquipamentoStatusDialog.defaultProps = {
  grupo: null,
  zIndex: undefined,
};

export default EquipamentoStatusDialog;
